import { Link } from 'react-router-dom';
import { ArrowRight, Bus, Camera, CheckCircle2, UserCheck, ClipboardList } from 'lucide-react';
import { useLang, useSeo } from '@/i18n/LanguageContext';
import PageHero from '@/components/PageHero';
import CtaBand from '@/components/CtaBand';
import { Reveal } from '@/components/Reveal';
import Icon from '@/components/Icon';

export default function Passengers() {
  const { content, route, lang } = useLang();
  useSeo(`${content.passengers.title} | Rodotrack`, content.passengers.subtitle);
  const c = content.passengers;

  return (
    <>
      <PageHero badge={c.eyebrow} title={c.title} subtitle={c.subtitle} />

      <section className="py-16 lg:py-20" data-testid="passengers-monitoring">
        <div className="container-x grid items-start gap-10 lg:grid-cols-2">
          <Reveal>
            <div data-testid="passengers-img-bus" className="overflow-hidden rounded-3xl">
              <img src={c.imgBus} alt={c.monitriip.title} loading="lazy" className="h-full w-full object-cover" style={{ minHeight: 360 }} />
            </div>
          </Reveal>
          <div>
            <Reveal>
              <span className="eyebrow"><Bus className="h-4 w-4" /> {c.monitriip.title}</span>
            </Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-4 font-display text-2xl font-bold leading-tight text-brand-navy sm:text-3xl">{c.monitriip.title}</h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-4 text-[15px] leading-relaxed text-brand-muted">{c.monitriip.desc}</p>
            </Reveal>
            <Reveal delay={0.15}>
              <ul className="mt-6 space-y-2.5">
                {c.monitriip.benefits.map((b, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-[14px] text-brand-navy/85">
                    <CheckCircle2 className="mt-0.5 h-[18px] w-[18px] shrink-0 text-brand-green" />
                    {b}
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="pb-16 lg:pb-20" data-testid="passengers-forwho">
        <div className="container-x grid gap-5 md:grid-cols-2">
          <Reveal>
            <div className="h-full rounded-2xl border border-brand-line bg-brand-mist p-8">
              <h3 className="flex items-center gap-2 font-display text-xl font-bold text-brand-navy">
                <UserCheck className="h-5 w-5 text-brand-blue" />
                {lang === 'es' ? '¿Para quién es?' : 'Para quem é'}
              </h3>
              <ul className="mt-5 space-y-2.5">
                {c.monitriip.forWho.map((b, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-[14px] text-brand-navy/85">
                    <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-brand-ice text-[9px] font-bold text-brand-blue">✓</span>
                    {b}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
          <Reveal delay={0.08}>
            <div className="h-full rounded-2xl border border-brand-line bg-brand-mist p-8">
              <h3 className="flex items-center gap-2 font-display text-xl font-bold text-brand-navy">
                <ClipboardList className="h-5 w-5 text-brand-blue" />
                {lang === 'es' ? '¿Cómo funciona?' : 'Como funciona'}
              </h3>
              <ol className="mt-5 space-y-3">
                {c.monitriip.how.map((b, i) => (
                  <li key={i} className="flex items-start gap-3 text-[14px] text-brand-navy/85">
                    <span className="font-mono text-sm font-bold text-brand-cyan">0{i + 1}</span>
                    {b}
                  </li>
                ))}
              </ol>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="pb-20 lg:pb-24" data-testid="passengers-dashcam">
        <div className="container-x grid items-start gap-10 lg:grid-cols-2">
          <div>
            <Reveal>
              <span className="eyebrow"><Camera className="h-4 w-4" /> Mov.IA</span>
            </Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-4 font-display text-2xl font-bold leading-tight text-brand-navy sm:text-3xl">{c.dashcam.title}</h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-4 text-[15px] leading-relaxed text-brand-muted">{c.dashcam.desc}</p>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="mt-6 grid gap-3 sm:grid-cols-2">
                {c.dashcam.features.map((f, i) => (
                  <div key={i} data-testid={`passengers-dashcam-feature-${i}`} className="rounded-xl border border-brand-line bg-white p-4 text-[13px] font-medium text-brand-navy">
                    {f}
                  </div>
                ))}
              </div>
            </Reveal>
            <Reveal delay={0.2}>
              <ul className="mt-5 space-y-2">
                {c.dashcam.benefits.map((b, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-[14px] text-brand-navy/85">
                    <CheckCircle2 className="mt-0.5 h-[18px] w-[18px] shrink-0 text-brand-green" />
                    {b}
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <div data-testid="passengers-img-dash" className="overflow-hidden rounded-3xl">
              <img src={c.imgDash} alt={c.dashcam.title} loading="lazy" className="h-full w-full object-cover" style={{ minHeight: 360 }} />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="pb-20 lg:pb-24">
        <div className="container-x">
          <Reveal>
            <div className="flex flex-col items-center justify-between gap-6 rounded-3xl bg-brand-mist p-8 text-center sm:flex-row sm:text-left lg:p-10">
              <p className="font-display text-xl font-bold text-brand-navy">{c.cta}</p>
              <Link to={route('contact')} data-testid="passengers-cta-btn" className="inline-flex items-center gap-2 rounded-full bg-brand-blue px-7 py-3.5 text-sm font-semibold text-white shadow-[0_16px_36px_-14px_rgba(30,159,224,0.9)] transition hover:bg-brand-bluedark">
                {content.header.quote}
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <div className="-mt-10"><CtaBand /></div>
    </>
  );
}